import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { useEntitlements } from "../lib/entitlements";
import { Users, ShoppingBag, ForkKnife, Gear, ChartBar, SignOut, QrCode, CalendarBlank, Phone, Lock, ChatCircleText, Sparkle, List, X, Rocket, ShieldCheck, Crown, Envelope, EyeSlash } from "@phosphor-icons/react";
import BuzzLogo from "../components/BuzzLogo";
import { toast } from "sonner";
import SupportChatWidget from "../components/SupportChatWidget";
import OnboardingCopilot from "../components/OnboardingCopilot";
import LocationSwitcher from "../components/LocationSwitcher";
import TrainingHealthChip from "../components/TrainingHealthChip";
import { api, isImpersonating, stopImpersonation } from "../lib/api";

const NavItem = ({ to, icon, label, end, locked, onClick, badge }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase()}`}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-[background-color,color] duration-200 ${
        isActive ? "bg-[#1E3A2F] text-white" : "text-[#121914] hover:bg-[#E6E2D6]"
      }`
    }
  >
    {icon}<span className="flex-1">{label}</span>
    {badge}
    {locked && <Lock size={12} className="opacity-60" />}
  </NavLink>
);

// Derive a compact "SMS: {state}" pill for the SMS Compliance nav item so
// operators see A2P approval progress at a glance on every dashboard page.
// - green   → any brand is APPROVED/VERIFIED AND at least one messaging service exists
// - amber   → any brand exists but none approved (still pending vetting)
// - red     → any brand has a failure_reason
// - grey    → nothing filed yet
const a2pPillFor = (a2p) => {
  if (!a2p) return null;
  const brands = a2p.brands || [];
  const services = a2p.messaging_services || [];
  if (brands.length === 0 && services.length === 0) {
    return { tone: "grey", label: "Not filed", tip: "A2P hasn't been started — click to open the wizard." };
  }
  const failed = brands.find((b) => b.failure_reason);
  if (failed) return { tone: "red", label: "Failed", tip: `Brand rejected: ${failed.failure_reason}` };
  const approved = brands.find((b) => (b.status || "").toUpperCase() === "APPROVED" || (b.status || "").toUpperCase() === "VERIFIED");
  if (approved && services.length > 0) return { tone: "green", label: "Live", tip: "Brand approved + messaging service linked. SMS is A2P-compliant." };
  if (approved) return { tone: "amber", label: "Almost", tip: "Brand approved — create a Messaging Service to finish." };
  return { tone: "amber", label: "Pending", tip: `Brand in review (${brands[0]?.status || "pending"}). 1–3 more days typical.` };
};

const A2PPill = ({ pill }) => {
  if (!pill) return null;
  const cls = {
    green: "bg-[#C2D1C1] text-[#1E3A2F]",
    amber: "bg-[#FEF3C7] text-[#92400E]",
    red: "bg-[#FEF2F0] text-[#A5382A]",
    grey: "bg-[#E6E2D6] text-[#5C6B61]",
  }[pill.tone] || "bg-[#E6E2D6] text-[#5C6B61]";
  return (
    <span title={pill.tip} className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${cls}`} data-testid={`a2p-pill-${pill.tone}`}>
      {pill.label}
    </span>
  );
};

const BottomTab = ({ to, icon, label, end }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={`bottomnav-${label.toLowerCase()}`}
    className={({ isActive }) =>
      `flex flex-col items-center gap-0.5 flex-1 py-2 text-[10px] ${
        isActive ? "text-[#1E3A2F]" : "text-[#5C6B61]"
      }`
    }
  >
    {icon}
    <span className="leading-none">{label}</span>
  </NavLink>
);

export default function DashboardLayout() {
  const { business, logout } = useAuth();
  const ent = useEntitlements();
  const nav = useNavigate();
  const publicUrl = `${window.location.origin}/join/${business.slug}`;
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [onbState, setOnbState] = useState(null);
  const [a2pStatus, setA2pStatus] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [inboxUnread, setInboxUnread] = useState(0);
  const impersonating = isImpersonating();

  useEffect(() => {
    api.get("/onboarding").then((r) => {
      setOnbState(r.data);
      const seen = sessionStorage.getItem("greetly-copilot-seen");
      if (!seen && r.data.percent < 50) { setCopilotOpen(true); sessionStorage.setItem("greetly-copilot-seen", "1"); }
    }).catch(() => {});
    // Poll A2P status once on mount + every 60s so the pill reflects Twilio
    // approvals without a page refresh (approvals land async, hours apart).
    const loadA2P = () => api.get("/twilio/a2p/status").then((r) => setA2pStatus(r.data)).catch(() => {});
    loadA2P();
    const t = setInterval(loadA2P, 60000);
    // whoami is a public probe that returns is_admin=true/false — only the
    // single super-admin email gets the Super-Admin section in the sidebar.
    // We skip this check while impersonating so a founder viewing-as-customer
    // sees exactly what that customer sees.
    let unreadTimer = null;
    if (!impersonating) {
      api.get("/admin/whoami").then((r) => {
        setIsAdmin(!!r.data.is_admin);
        if (r.data.is_admin) {
          const loadUnread = () => api.get("/admin/inbox/unread_count")
            .then((res) => setInboxUnread(res.data.unread || 0))
            .catch(() => {});
          loadUnread();
          unreadTimer = setInterval(loadUnread, 60000);
        }
      }).catch(() => setIsAdmin(false));
    }
    return () => {
      clearInterval(t);
      if (unreadTimer) clearInterval(unreadTimer);
    };
  }, [impersonating]);

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Public join link copied");
  };

  const en = ent.entitled || {};
  const closeDrawer = () => setDrawerOpen(false);

  const SideNavContent = ({ onNavigate }) => (
    <>
      <Link to="/" className="flex items-center gap-2 mb-8" onClick={onNavigate}>
        <div className="w-8 h-8 rounded-lg bg-[#0E1512] flex items-center justify-center">
          <BuzzLogo size={22} />
        </div>
        <span className="font-heading font-extrabold text-lg">BuzzerAI</span>
      </Link>
      <div className="mb-6">
        <div className="overline">Business</div>
        <LocationSwitcher business={business} />
        <div className="text-xs text-[#5C6B61] capitalize">{business.industry}</div>
        <TrainingHealthChip />
        {ent.is_trial && <div className="mt-2 tag tag-notified inline-block">Trial: all add-ons</div>}
      </div>
      <nav className="space-y-1 flex-1">
        <NavItem to="/dashboard" end icon={<Users size={18} />} label="Waitlist" onClick={onNavigate} />
        <NavItem to="/dashboard/schedule" icon={<CalendarBlank size={18} />} label="Schedule" locked={!en.scheduling} onClick={onNavigate} />
        <NavItem to="/dashboard/voice" icon={<Phone size={18} />} label="Voice" locked={!en.ai_voice} onClick={onNavigate} />
        <NavItem to="/dashboard/mimi" icon={<Sparkle size={18} weight="fill" />} label="Mimi's Training" onClick={onNavigate} />
        <NavItem to="/dashboard/a2p" icon={<ChatCircleText size={18} />} label="SMS Compliance" onClick={onNavigate} badge={<A2PPill pill={a2pPillFor(a2pStatus)} />} />
        <NavItem to="/dashboard/analytics" icon={<ChartBar size={18} />} label="Analytics" onClick={onNavigate} />
        <NavItem to="/dashboard/feedback" icon={<ChatCircleText size={18} />} label="Feedback" onClick={onNavigate} />
        <NavItem to="/dashboard/go-live" icon={<Rocket size={18} />} label="Go Live" onClick={onNavigate} />
        <NavItem to="/dashboard/settings" icon={<Gear size={18} />} label="Settings" onClick={onNavigate} />
        <NavItem to="/dashboard/admin" icon={<ShieldCheck size={18} weight="fill" />} label="Manager Room" onClick={onNavigate} />
      </nav>
      {isAdmin && !impersonating && (
        <div className="mt-4 pt-4 border-t border-[#E1DFD5]">
          <div className="overline mb-2 flex items-center gap-1 text-[#D96C5B]">
            <Crown size={12} weight="fill" /> Super-Admin
          </div>
          <div className="space-y-1">
            <NavItem to="/admin/customers" icon={<Users size={18} weight="fill" />} label="Customers" onClick={onNavigate} />
            <NavItem
              to="/admin/inbox"
              icon={<Envelope size={18} weight="fill" />}
              label="Support Inbox"
              onClick={onNavigate}
              badge={inboxUnread > 0 ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#D96C5B] text-white" data-testid="inbox-unread-badge">{inboxUnread}</span>
              ) : null}
            />
            <NavItem to="/admin/beta" icon={<Sparkle size={18} weight="fill" />} label="Beta Applications" onClick={onNavigate} />
          </div>
        </div>
      )}
      <div className="pt-6 border-t border-[#E1DFD5] space-y-2">
        <button onClick={() => { setCopilotOpen(true); onNavigate?.(); }} className="w-full flex items-center gap-2 text-sm text-[#1E3A2F] px-4 py-2 rounded-lg bg-[#C2D1C1]/40 hover:bg-[#C2D1C1] transition-colors" data-testid="open-copilot">
          <Sparkle size={16} weight="fill" color="#D96C5B" />
          <span className="flex-1 text-left">Setup Copilot</span>
          {onbState && onbState.done < onbState.total && (
            <span className="tag tag-cancelled text-[10px]" data-testid="copilot-badge">{onbState.total - onbState.done} left</span>
          )}
        </button>
        <Link to="/pricing" className="w-full flex items-center gap-2 text-sm text-[#D96C5B] px-4 py-2 rounded-lg hover:bg-[#E6E2D6] transition-colors" data-testid="build-plan" onClick={onNavigate}>
          <Lock size={14} /> Build your plan
        </Link>
        <button data-testid="copy-link" onClick={() => { copyLink(); onNavigate?.(); }} className="w-full flex items-center gap-2 text-sm text-[#1E3A2F] px-4 py-2 rounded-lg hover:bg-[#E6E2D6] transition-colors">
          <QrCode size={16} /> Copy join link
        </button>
        <button data-testid="logout" onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-2 text-sm text-[#5C6B61] px-4 py-2 rounded-lg hover:bg-[#E6E2D6] transition-colors">
          <SignOut size={16} /> Log out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F7F6F2] grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      {impersonating && (
        <div className="col-span-full bg-[#D96C5B] text-white text-xs sm:text-sm px-4 sm:px-6 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="impersonation-banner">
          <div className="flex items-center gap-2">
            <EyeSlash size={16} weight="fill" />
            <span>
              Viewing as <b>{business.business_name}</b> — every action you take will affect their real account.
            </span>
          </div>
          <button
            onClick={() => { stopImpersonation(); window.location.href = "/admin/customers"; }}
            className="bg-white text-[#D96C5B] px-3 py-1 rounded-full font-bold hover:bg-white/90"
            data-testid="stop-impersonation-btn"
          >
            ← Return to Super-Admin
          </button>
        </div>
      )}
      {business?.suspended && !impersonating && (
        <div className="col-span-full bg-[#A5382A] text-white text-xs sm:text-sm px-4 sm:px-6 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="suspended-banner">
          <div className="flex items-center gap-2">
            <span className="font-bold uppercase tracking-wide">Account disconnected</span>
            <span>
              Reason: {business.suspended_reason || "not provided"}. AI voice and public waitlist are paused. Contact support to restore service.
            </span>
          </div>
          <a href="mailto:support@buzzerai.org" className="bg-white text-[#A5382A] px-3 py-1 rounded-full font-bold hover:bg-white/90">
            Contact support
          </a>
        </div>
      )}
      <div className="col-span-full bg-[#EEE0B8] text-[#7A5A00] text-[11px] sm:text-xs px-4 sm:px-6 py-2 text-center leading-snug">
        <b>Pre-launch draft:</b> Terms &amp; Privacy under attorney review. <Link to="/terms" className="underline">Terms</Link> · <Link to="/privacy" className="underline">Privacy</Link>
      </div>

      {/* Mobile top bar */}
      <header className="lg:hidden col-span-full flex items-center justify-between bg-white border-b border-[#E1DFD5] px-4 py-3 sticky top-0 z-30" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <button onClick={() => setDrawerOpen(true)} className="w-10 h-10 rounded-lg hover:bg-[#E6E2D6] flex items-center justify-center" data-testid="mobile-menu-btn" aria-label="Open menu">
          <List size={22} />
        </button>
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#0E1512] flex items-center justify-center">
            <BuzzLogo size={20} />
          </div>
          <span className="font-heading font-extrabold">{business.business_name}</span>
        </Link>
        <button onClick={() => setCopilotOpen(true)} className="w-10 h-10 rounded-lg bg-[#C2D1C1]/40 flex items-center justify-center" data-testid="mobile-copilot-btn" aria-label="Setup Copilot">
          <Sparkle size={18} weight="fill" color="#D96C5B" />
        </button>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col bg-white border-r border-[#E1DFD5] p-6">
        <SideNavContent />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-[#121914]/40" onClick={closeDrawer} />
          <aside className="absolute inset-y-0 left-0 w-[85%] max-w-[320px] bg-white p-6 flex flex-col overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <span className="overline">Menu</span>
              <button onClick={closeDrawer} className="w-10 h-10 rounded-full hover:bg-[#F1D7D2]" data-testid="mobile-drawer-close" aria-label="Close menu"><X size={18} className="mx-auto" /></button>
            </div>
            <SideNavContent onNavigate={closeDrawer} />
          </aside>
        </div>
      )}

      <main className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">
        <Outlet />
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#E1DFD5] flex" style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom))" }} data-testid="mobile-bottom-nav">
        <BottomTab to="/dashboard" end icon={<Users size={20} />} label="Waitlist" />
        <BottomTab to="/dashboard/voice" icon={<Phone size={20} />} label="Voice" />
        <BottomTab to="/dashboard/analytics" icon={<ChartBar size={20} />} label="Stats" />
        <BottomTab to="/dashboard/settings" icon={<Gear size={20} />} label="Settings" />
      </nav>

      <OnboardingCopilot open={copilotOpen} onClose={() => setCopilotOpen(false)} />
      <SupportChatWidget />
    </div>
  );
}
